import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Layers, Camera, LayoutGrid, Pin, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { API, STAGES, STATUS_META, lotLabel } from './constants';

const StepDots = ({ steps, size = 8, testid }) => (
  <span className="inline-flex items-center gap-1" data-testid={testid}>
    {STAGES.map((s) => {
      const st = STATUS_META[steps?.[s.id] || 'todo'];
      return <span key={s.id} title={`${s.n} · ${s.label} — ${st.label}`} style={{ width: size, height: size, borderRadius: 999, background: st.color, display: 'inline-block' }} />;
    })}
  </span>
);

/** V15.21 — Zone 1 : barre de lot sticky (sélecteur, avancement 1→4, modes secondaires). */
/** V15.26 — Pastille « N surcharges » + retrait global des surcharges matériaux du lot. */
const OverridesBadge = ({ projectId, lot, onCleared }) => {
  const [busy, setBusy] = useState(false);
  if (!lot?.overrides_count) return null;
  const clear = async () => {
    if (!window.confirm(`Retirer les ${lot.overrides_count} surcharge(s) du lot ${lot.lot_number || ''} ? Il suivra à nouveau les matériaux du projet.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/projects/${projectId}/lots/${lot.lot_id}/material-overrides`, { withCredentials: true });
      toast.success('Surcharges retirées : le lot suit le projet');
      onCleared?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retrait impossible');
    } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] pl-2 pr-1 py-0.5 rounded-full" style={{ background: 'rgba(217,160,91,0.16)', color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.5)' }}
          title="Matériaux surchargés pour ce lot uniquement" data-testid="lot-overrides-badge">
      <Pin className="w-3 h-3" /> {lot.overrides_count} surcharge{lot.overrides_count > 1 ? 's' : ''}
      <button type="button" onClick={clear} disabled={busy} className="ml-0.5 rounded-full p-0.5 hover:bg-white/10 disabled:opacity-50" title="Tout retirer" data-testid="lot-overrides-clear-btn"><X className="w-3 h-3" /></button>
    </span>
  );
};

export const LotHeaderBar = ({ projectId, lots, activeLotId, onSelectLot, onOpenBatch, onOpenFps, projectView, onToggleProjectView, onRefresh }) => {
  const idx = lots.findIndex((l) => l.lot_id === activeLotId);
  const lot = lots[idx] || null;
  const go = (d) => { const n = lots[idx + d]; if (n) onSelectLot(n.lot_id); };
  return (
    <div className="sticky top-0 z-20 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap"
         style={{ background: 'rgba(16,17,20,0.92)', border: '1px solid var(--c-border)', backdropFilter: 'blur(12px)' }}
         data-testid="production-lot-header">
      <span className="hud-eyebrow hud-eyebrow-gold">Lot en production</span>
      <button type="button" onClick={() => go(-1)} disabled={idx <= 0} className="p-1 rounded disabled:opacity-30 hover:bg-white/5" title="Lot précédent" data-testid="lot-header-prev-btn">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <Select value={activeLotId || ''} onValueChange={onSelectLot}>
        <SelectTrigger className="w-[320px] bg-white/[0.04] border-white/10 text-white h-9" data-testid="production-lot-selector">
          <SelectValue placeholder="— Choisir un lot —" />
        </SelectTrigger>
        <SelectContent>
          {lots.map((l) => (
            <SelectItem key={l.lot_id} value={l.lot_id} data-testid={`production-lot-option-${l.lot_id}`}>
              <span className="inline-flex items-center gap-2">
                <StepDots steps={l.steps} size={6} />
                <span>{lotLabel(l)}{l.floor_name ? ` · ${l.floor_name}` : ''}{!l.has_structure ? ' · sans tracé' : ''}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button type="button" onClick={() => go(1)} disabled={idx < 0 || idx >= lots.length - 1} className="p-1 rounded disabled:opacity-30 hover:bg-white/5" title="Lot suivant" data-testid="lot-header-next-btn">
        <ChevronRight className="w-4 h-4" />
      </button>
      {lot && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="production-lot-progress">
          {STAGES.map((s) => {
            const st = STATUS_META[lot.steps?.[s.id] || 'todo'];
            return (
              <span key={s.id} className="inline-flex items-center gap-1.5 text-[10.5px] px-2 py-0.5 rounded-full"
                    style={{ background: st.bg, color: st.color, border: `1px solid ${st.color}55` }} data-testid={`lot-progress-${s.id}`}>
                <span className="font-mono font-semibold">{s.n}</span> {s.short} · {s.optional && (lot.steps?.[s.id] || 'todo') === 'todo' ? 'Optionnel' : st.label}
              </span>
            );
          })}
          {lot.lot_complete && (
            <span className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: 'rgba(134,239,172,0.18)', color: '#86EFAC', border: '1px solid rgba(134,239,172,0.5)' }}
                  title="Étape 3 validée : le lot est livrable (Agrandir en 4K). L'étape 4 est une variante d'ambiance facultative." data-testid="lot-complete-badge">
              ✓ Lot livrable
            </span>
          )}
          <span className="text-[10.5px] font-mono" style={{ color: 'var(--c-text-faint)' }}>{lot.renders} rendu{lot.renders > 1 ? 's' : ''}</span>
          <OverridesBadge projectId={projectId} lot={lot} onCleared={onRefresh} />
        </div>
      )}
      <div className="ml-auto flex items-center gap-1.5">
        <button type="button" onClick={onToggleProjectView} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors hover:bg-white/5"
                style={{ borderColor: projectView ? 'var(--c-gold)' : 'var(--c-border)', color: projectView ? 'var(--c-gold)' : 'var(--c-text-2)' }}
                data-testid="btn-toggle-project-view" aria-pressed={projectView}>
          <LayoutGrid className="w-3.5 h-3.5" /> Vue projet (tous les lots)
        </button>
        <button type="button" onClick={onOpenBatch} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border hover:bg-white/5"
                style={{ borderColor: 'var(--c-border)', color: 'var(--c-text-2)' }} data-testid="btn-trigger-batch-mode">
          <Layers className="w-3.5 h-3.5" /> Batch multi-lots
        </button>
        <button type="button" onClick={onOpenFps} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border hover:bg-white/5"
                style={{ borderColor: 'var(--c-border)', color: 'var(--c-text-2)' }} data-testid="btn-trigger-fps-mode">
          <Camera className="w-3.5 h-3.5" /> Caméra FPS
        </button>
      </div>
    </div>
  );
};

export default LotHeaderBar;
